import React, { useState } from 'react';
import type { OptimizeResponse, StoreResult } from '../types/optimize';

interface Props {
  data: OptimizeResponse;
  groupStores: string[];
  compareStore: string;
  onBack: () => void;
}

type TabId = 'stores' | 'single';

const fmt = (n: any) => `€${parseFloat(String(n || 0)).toFixed(2)}`;

// ── Expandable Store Card ─────────────────────────────────────────────────────
const StoreCard = ({ store, unavailable }: { store: StoreResult; unavailable: { productName: string }[] }) => {
  const [open, setOpen] = useState(store.assignments.length > 0);
  const hasWins = store.assignments.length > 0;

  return (
    <div style={CS.card}>
      <div style={CS.cardHeader} onClick={() => setOpen(o => !o)}>
        <div style={CS.cardLeft}>
          <div style={CS.storeName}>{store.companyName}</div>
          <div style={CS.storeMeta}>
            {hasWins
              ? <span style={CS.badgeGreen}>{store.assignments.length} φθηνότερα προϊόντα</span>
              : store.noWinsButHasProducts
                ? <span style={CS.badgeGray}>Διαθέτει ακριβότερα προϊόντα</span>
                : <span style={CS.badgeGray}>Χωρίς διαθέσιμα προϊόντα</span>
            }
            <span style={CS.coverage}>{store.availableCount}/{store.totalProducts} διαθέσιμα</span>
          </div>
        </div>
        <div style={CS.cardRight}>
          {hasWins && <span style={CS.subtotal}>{fmt(store.subtotal)}</span>}
          <span style={CS.arrow}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div style={CS.cardBody}>
          {hasWins ? (
            <table style={CS.table}>
              <thead>
                <tr>
                  <th style={CS.th}>Προϊόν</th>
                  <th style={{ ...CS.th, textAlign: 'right' }}>Τιμή</th>
                </tr>
              </thead>
              <tbody>
                {store.assignments.map((a, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#F8FBFD' : 'white' }}>
                    <td style={CS.td}>
                      {a.productName}
                      {a.isIsotimia && a.isotimiaWith && a.isotimiaWith.length > 0 && (
                        <span style={CS.isotimia}> ίδια τιμή και στο: {a.isotimiaWith.join(', ')}</span>
                      )}
                    </td>
                    <td style={{ ...CS.td, textAlign: 'right', fontWeight: 600, color: '#27AE60' }}>
                      {fmt(a.price)}
                    </td>
                  </tr>
                ))}
                {unavailable.map((u, i) => (
                  <tr key={`u-${i}`} style={{ background: '#FEF9F9' }}>
                    <td colSpan={2} style={{ ...CS.td, color: '#E74C3C', fontSize: 12 }}>
                      ⚠ {u.productName} — Μη διαθέσιμο στα επιλεγμένα καταστήματα
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...CS.td, fontWeight: 700, color: '#2952CC' }}>Υποσύνολο</td>
                  <td style={{ ...CS.td, fontWeight: 700, color: '#2952CC', textAlign: 'right' }}>{fmt(store.subtotal)}</td>
                </tr>
              </tfoot>
            </table>
          ) : store.noWinsButHasProducts ? (
            <div style={CS.noWinsMsg}>
              Αυτό το κατάστημα διαθέτει προϊόντα της λίστας σου, αλλά σε ακριβότερες τιμές από άλλα καταστήματα.
            </div>
          ) : (
            <div style={CS.noWinsMsg}>
              Κανένα προϊόν της λίστας δεν βρέθηκε σε αυτό το κατάστημα.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Single Store Card ─────────────────────────────────────────────────────────
const SingleStoreCard = ({ data }: { data: OptimizeResponse }) => {
  const [open, setOpen] = useState(true);
  const ss = data.singleStoreResult;
  if (!ss) return null;

  return (
    <div style={{ ...CS.card, borderColor: '#5D6D7E' }}>
      <div style={{ ...CS.cardHeader, background: '#5D6D7E' }} onClick={() => setOpen(o => !o)}>
        <div style={CS.cardLeft}>
          <div style={{ ...CS.storeName, color: 'white' }}>{ss.companyName}</div>
          <div style={{ fontSize: 12, color: '#D5DBDB', marginTop: 2 }}>
            {ss.coveredCount}/{data.storeResults[0]?.totalProducts || 0} διαθέσιμα
            {data.singleStorePartial && ' — μερική κάλυψη'}
          </div>
        </div>
        <div style={CS.cardRight}>
          <span style={{ ...CS.subtotal, color: 'white' }}>{fmt(ss.total)}</span>
          <span style={{ ...CS.arrow, color: 'white' }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && (
        <div style={CS.cardBody}>
          <table style={CS.table}>
            <thead>
              <tr>
                <th style={CS.th}>Προϊόν</th>
                <th style={{ ...CS.th, textAlign: 'right' }}>Τιμή</th>
              </tr>
            </thead>
            <tbody>
              {ss.rows.map((row, i) => (
                <tr key={i} style={{ background: row.price === null ? '#FEF9F9' : i % 2 === 0 ? '#F8FBFD' : 'white' }}>
                  <td style={CS.td}>
                    {row.productName}
                    {row.price === null && (
                      <span style={{ color: '#E74C3C', fontSize: 12, marginLeft: 8 }}>
                        — Μη διαθέσιμο στο {ss.companyName}
                      </span>
                    )}
                  </td>
                  <td style={{ ...CS.td, textAlign: 'right', fontWeight: row.price ? 600 : 400, color: row.price ? '#2952CC' : '#E74C3C' }}>
                    {row.price !== null ? fmt(row.price) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ ...CS.td, fontWeight: 700, color: '#2952CC' }}>
                  Σύνολο{data.singleStorePartial ? ' (μερικό)' : ''}
                </td>
                <td style={{ ...CS.td, fontWeight: 700, color: '#2952CC', textAlign: 'right' }}>{fmt(ss.total)}</td>
              </tr>
            </tfoot>
          </table>
          {data.singleStorePartial && (
            <div style={{ ...CS.noWinsMsg, color: '#E74C3C', background: '#FEF9F9' }}>
              ⚠ Η τιμή {fmt(ss.total)} αφορά μόνο τα {ss.coveredCount} διαθέσιμα προϊόντα.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const OptimizeResults: React.FC<Props> = ({ data, groupStores, compareStore, onBack }) => {
  const [tab, setTab] = useState<TabId>('stores');
  const totalProducts = data.storeResults[0]?.totalProducts ?? 0;

  // Determine comparison result
  const diff = data.singleStoreCost - data.optimalCost;
  const singleIsCheaper = diff < 0;
  const noSavings = Math.abs(diff) < 0.01;

  return (
    <div style={S.page}>
      <button onClick={onBack} style={S.back}>← Αλλαγή επιλογών</button>
      <h1 style={S.h1}>💡 Αποτελέσματα Σύγκρισης</h1>
      <p style={S.sub}>Λίστα: <strong>{data.listName}</strong> • {totalProducts} προϊόντα</p>

      {/* Warnings */}
      {data.warnings.length > 0 && (
        <div style={S.warningsBox}>
          {data.warnings.map((w, i) => (
            <div key={i} style={S.warningRow}>⚠ {w}</div>
          ))}
        </div>
      )}

      {/* Summary cards — always shown */}
      <div style={S.summaryBox}>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>💰 Βέλτιστο Κόστος (Υ)</div>
          <div style={{ ...S.metricValue, color: '#27AE60' }}>{fmt(data.optimalCost)}</div>
          <div style={S.metricNote}>αγοράζοντας από {groupStores.length} κατάστημα{groupStores.length !== 1 ? 'τα' : ''}</div>
        </div>

        <div style={S.metricCard}>
          <div style={S.metricLabel}>🏪 Μόνο από {compareStore} (Χ)</div>
          <div style={{ ...S.metricValue, color: '#E74C3C' }}>{fmt(data.singleStoreCost)}</div>
          {data.singleStorePartial && <div style={{ fontSize: 11, color: '#E74C3C', marginTop: 4 }}>⚠ μερική κάλυψη</div>}
        </div>

        {/* Comparison result — always shown */}
        {noSavings ? (
          <div style={{ ...S.metricCard, background: '#F8F9FA' }}>
            <div style={S.metricLabel}>📊 Σύγκριση (Χ − Υ)</div>
            <div style={{ ...S.metricValue, color: '#888', fontSize: 20 }}>Ίδια τιμή</div>
            <div style={S.metricNote}>Δεν υπάρχει διαφορά</div>
          </div>
        ) : singleIsCheaper ? (
          <div style={{ ...S.metricCard, background: '#FEF9F9', border: '1.5px solid #FADBD8' }}>
            <div style={S.metricLabel}>⚠️ Σύγκριση (Χ − Υ)</div>
            <div style={{ ...S.metricValue, color: '#E74C3C' }}>−{fmt(Math.abs(diff))}</div>
            <div style={{ fontSize: 11, color: '#E74C3C', marginTop: 4 }}>
              Το {compareStore} είναι φθηνότερο κατά {fmt(Math.abs(diff))}!
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
              💡 Δοκίμασε να προσθέσεις το {compareStore} στην ομάδα καταστημάτων
            </div>
          </div>
        ) : (
          <div style={{ ...S.metricCard, background: '#EAFAF1', border: '1.5px solid #A9DFBF' }}>
            <div style={S.metricLabel}>💚 Εξοικονόμηση (Χ − Υ)</div>
            <div style={{ ...S.metricValue, color: '#27AE60' }}>{fmt(diff)}</div>
            <div style={S.metricNote}>αγοράζοντας από πολλά καταστήματα</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {(['stores', 'single'] as TabId[]).map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ ...S.tabBtn, ...(tab === t ? S.tabActive : {}) }}>
            {{ stores: '📋 Ανά Κατάστημα', single: `🏪 Αγορά μόνο από ${compareStore}` }[t]}
          </button>
        ))}
      </div>

      {tab === 'stores' && (
        <div>
          {data.storeResults.map(store => (
            <StoreCard key={store.companyName} store={store} unavailable={data.unavailableProducts} />
          ))}
          {data.unavailableProducts.length > 0 && (
            <div style={S.unavailBox}>
              <div style={S.unavailTitle}>⚠ Μη διαθέσιμα σε κανένα επιλεγμένο κατάστημα</div>
              {data.unavailableProducts.map((u, i) => (
                <div key={i} style={S.unavailRow}>• {u.productName} — <em>ελέγξτε σε άλλο κατάστημα</em></div>
              ))}
            </div>
          )}
          <div style={S.totalRow}>
            <span>Βέλτιστο σύνολο (Υ)</span>
            <span style={{ fontWeight: 700, color: '#27AE60', fontSize: 20 }}>{fmt(data.optimalCost)}</span>
          </div>
        </div>
      )}

      {tab === 'single' && <SingleStoreCard data={data} />}

      <button onClick={() => window.print()} style={S.printBtn}>🖨️ Εκτύπωση / Αποθήκευση PDF</button>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  page: { maxWidth: 860, margin: '0 auto', padding: '24px 16px', fontFamily: 'Arial, sans-serif' },
  back: { background: 'none', border: 'none', color: '#2952CC', cursor: 'pointer', fontSize: 14, marginBottom: 16, padding: 0 },
  h1: { color: '#2952CC', fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub: { color: '#5D6D7E', fontSize: 14, marginBottom: 20 },
  warningsBox: { background: '#FAEEDA', borderLeft: '4px solid #E67E22', borderRadius: 6, padding: '10px 14px', marginBottom: 16 },
  warningRow: { fontSize: 12, color: '#633806', padding: '2px 0' },
  summaryBox: { display: 'flex', flexDirection: 'column' as const, gap: 12, marginBottom: 20 },
  metricCard: { width: '100%', background: 'white', borderRadius: 10, padding: '14px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' },
  metricLabel: { fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 4 },
  metricValue: { fontSize: 26, fontWeight: 700 },
  metricNote: { fontSize: 11, color: '#888', marginTop: 4 },
  tabs: { display: 'flex', gap: 0, borderBottom: '2px solid #E8EEF2', marginBottom: 16, overflowX: 'auto' as const },
  tabBtn: { padding: '9px 16px', fontSize: 13, background: 'none', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', color: '#888', marginBottom: -2, whiteSpace: 'nowrap' as const },
  tabActive: { color: '#2952CC', borderBottomColor: '#2952CC', fontWeight: 600 },
  unavailBox: { background: '#FEF9F9', border: '1px solid #FADBD8', borderRadius: 8, padding: '12px 16px', marginBottom: 12 },
  unavailTitle: { fontWeight: 600, color: '#E74C3C', marginBottom: 8, fontSize: 13 },
  unavailRow: { fontSize: 13, color: '#888', padding: '3px 0' },
  totalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', borderRadius: 8, padding: '14px 18px', marginTop: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', fontWeight: 600, color: '#2952CC' },
  printBtn: { padding: '10px 24px', background: 'white', color: '#2952CC', border: '2px solid #2952CC', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 16 },
};

const CS: Record<string, React.CSSProperties> = {
  card: { background: 'white', borderRadius: 10, marginBottom: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', border: '1px solid #E8EEF2' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#2952CC', cursor: 'pointer', userSelect: 'none' as const },
  cardLeft: { display: 'flex', flexDirection: 'column' as const, gap: 4 },
  cardRight: { display: 'flex', alignItems: 'center', gap: 12 },
  storeName: { color: 'white', fontWeight: 700, fontSize: 15 },
  storeMeta: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' as const },
  badgeGreen: { fontSize: 11, background: 'rgba(255,255,255,0.2)', color: '#ABEBC6', borderRadius: 4, padding: '2px 7px' },
  badgeGray: { fontSize: 11, background: 'rgba(255,255,255,0.1)', color: '#B3C6FF', borderRadius: 4, padding: '2px 7px' },
  coverage: { fontSize: 11, color: '#B3C6FF' },
  subtotal: { color: 'white', fontWeight: 700, fontSize: 18 },
  arrow: { color: 'white', fontSize: 12 },
  cardBody: { padding: 0 },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: { padding: '7px 14px', fontSize: 11, fontWeight: 700, color: '#888', textAlign: 'left' as const, borderBottom: '1px solid #E8EEF2', textTransform: 'uppercase' as const },
  td: { padding: '9px 14px', fontSize: 13, color: '#2952CC', borderBottom: '1px solid #F0F4F8' },
  isotimia: { fontSize: 11, color: '#1A3FA8', marginLeft: 6, fontStyle: 'italic' },
  noWinsMsg: { padding: '12px 16px', fontSize: 13, color: '#888', background: '#F8FBFD', fontStyle: 'italic' },
};

export default OptimizeResults;
